
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as exposed from '../exposed'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {Response} from '../core/Response';
import {FieldModelResponse} from '../core/Response';
import {LogoutReason} from './LogoutReason';
import {FieldModelLogoutReason} from './LogoutReason';

/**
 * LogoutResponse struct
 */
class LogoutResponse extends Response {
  /**
   * Initialize struct
   * @param {!Response=} parent
   * @param {!string=} message
   * @param {!LogoutReason=} reason
   * @constructor
   */
  constructor (parent = new Response(), message = '', reason = new LogoutReason()) {
    super()
    super.copy(parent)
    this.Message = message
    this.Reason = reason
  }

  /**
   * Copy struct (shallow copy)
   * @this {!LogoutResponse}
   * @param {!LogoutResponse} other Other struct
   * @returns {!LogoutResponse} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Message != null) {
      this.Message = other.Message
    } else {
      this.Message = null
    }
    if (other.Reason != null) {
      this.Reason = LogoutReason.fromObject(other.Reason)
    } else {
      this.Reason = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!LogoutResponse}
   * @returns {!LogoutResponse} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new LogoutResponseModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new LogoutResponseModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!LogoutResponse}
   * @param {!LogoutResponse} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof LogoutResponse)) {
      throw new TypeError('Instance of LogoutResponse is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!LogoutResponse}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Message: ((this.Message != null) ? this.Message : null),
      Reason: ((this.Reason != null) ? this.Reason : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return LogoutResponse.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!LogoutResponse} other Object value
   * @returns {!LogoutResponse} Created struct
   */
  static fromObject (other) {
    return new LogoutResponse().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!LogoutResponse}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return LogoutResponse.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!LogoutResponse}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 5007
  }
}

export { LogoutResponse };

/**
 * Fast Binary Encoding LogoutResponse field model
 */
class FieldModelLogoutResponse extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelResponse(buffer, 4 + 4)
    this.Message = new fbe.FieldModelString(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
    this.Reason = new FieldModelLogoutReason(buffer, this.Message.fbeOffset + this.Message.fbeSize)
  }

  /**
   * Get the core.Response field model
   * @this {!FieldModelLogoutResponse}
   * @returns {!FieldModelResponse} core.Response field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelLogoutResponse}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelLogoutResponse}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Message.fbeSize + this.Reason.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelLogoutResponse}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Message.fbeExtra + this.Reason.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelLogoutResponse}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelLogoutResponse.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelLogoutResponse}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 5007
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelLogoutResponse}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelLogoutResponse}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Message.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Message.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Message.fbeSize

    if ((fbeCurrentSize + this.Reason.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Reason.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Reason.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelLogoutResponse}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelLogoutResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelLogoutResponse}
   * @param {!LogoutResponse} fbeValue Default value, defaults is new LogoutResponse()
   * @returns {!LogoutResponse} LogoutResponse value
   */
  get (fbeValue = new LogoutResponse()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelLogoutResponse}
   * @param {!LogoutResponse} fbeValue LogoutResponse value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Message.fbeSize) <= fbeStructSize) {
      fbeValue.Message = this.Message.get()
    } else {
      fbeValue.Message = ''
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Message.fbeSize

    if ((fbeCurrentSize + this.Reason.fbeSize) <= fbeStructSize) {
      fbeValue.Reason = this.Reason.get()
    } else {
      fbeValue.Reason = new LogoutReason()
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Reason.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelLogoutResponse}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelLogoutResponse}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelLogoutResponse}
   * @param {!LogoutResponse} fbeValue LogoutResponse value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelLogoutResponse}
   * @param {LogoutResponse} fbeValue LogoutResponse value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Message.set(fbeValue.Message)
    this.Reason.set(fbeValue.Reason)
  }
}

export { FieldModelLogoutResponse };

/**
 * Fast Binary Encoding LogoutResponse model
 */
class LogoutResponseModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelLogoutResponse(this.buffer, 4)
  }

  /**
   * Get the LogoutResponse model
   * @this {!LogoutResponseModel}
   * @returns {!FieldModelLogoutResponse} model LogoutResponse model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!LogoutResponseModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!LogoutResponseModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return LogoutResponseModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!LogoutResponseModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelLogoutResponse.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!LogoutResponseModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!LogoutResponseModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!LogoutResponseModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!LogoutResponseModel}
   * @param {!LogoutResponse} value LogoutResponse value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!LogoutResponseModel}
   * @param {!LogoutResponse} value LogoutResponse value, defaults is new LogoutResponse()
   * @return {!object} Deserialized LogoutResponse value and its size
   */
  deserialize (value = new LogoutResponse()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new LogoutResponse(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new LogoutResponse(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!LogoutResponseModel}
   * @param {!number} prev Previous LogoutResponse model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { LogoutResponseModel };
